/**
 * React Hook for Drill Operations
 * 
 * Provides a convenient hook interface to all drill operations,
 * automatically using the scoped database from AuthContext.
 * 
 * IMPORTANT: Write operations (create, update, delete, import) require
 * Administrator privileges. The isAdmin flag is passed from AdminContext.
 */

import { useMemo } from 'react';
import { useScopedDb } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAdmin } from '@/contexts/AdminContext';
import { createDrillDatabase } from './drill-database';
import { createDrillOperations } from './drill-operations';

/**
 * Hook to access drill operations with automatic scoped database
 * Write operations require Administrator privileges
 */
export function useDrillOperations() {
  const scopedDb = useScopedDb();
  const { user } = useAuth();
  const { isAuthorizedAdmin, isAdminMode } = useAdmin();
  
  // User must be both authorized as admin AND have admin mode enabled
  // to perform write operations on the shared drill library
  const canModifyDrills = isAuthorizedAdmin && isAdminMode;
  
  // Create memoized instances with admin flag
  const drillDb = useMemo(
    () => createDrillDatabase(scopedDb as any, user?.uid, canModifyDrills),
    [scopedDb, user?.uid, canModifyDrills]
  );
  
  const drillOps = useMemo(
    () => createDrillOperations(drillDb),
    [drillDb]
  );
  
  return {
    db: drillDb,
    ops: drillOps,
    canModifyDrills  // Expose this for UI to check
  };
}

/**
 * Hook to listen to drills with automatic updates
 */
export function useDrillListener(callback: (drills: any[]) => void) {
  const { db } = useDrillOperations();
  
  return useMemo(
    () => db.listenToDrills(callback),
    [db, callback]
  );
}

/**
 * Hook to listen to categories with automatic updates
 */
export function useCategoryListener(callback: (categories: any[]) => void) {
  const { db } = useDrillOperations();
  
  return useMemo(
    () => db.listenToCategories(callback),
    [db, callback]
  );
}
